import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo, useState } from "react";
import CloseDialogButton from "../../../components/CloseDialogButton";
import { actionBtnSx } from "./actionButtonStyles";
import { useAuth } from "../../../contexts/AuthContext";
import { getAllBrands } from "../../../services/brandService";
import { getAllRestaurants } from "../../../services/restaurantService";
import {
  createUser,
  deleteUser,
  getAllUsers,
  resendInvite,
  updateUser,
} from "../../../services/userService";
import ConfirmDialog from "./ConfirmDialog";
import ResendInviteDialog from "./ResendInviteDialog";

const UsersPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { auth } = useAuth();
  const currentUserId = auth?.user?.id;
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [brandFilter, setBrandFilter] = useState("all"); // "all" | "unassigned" | brandId
  const [roleFilter, setRoleFilter] = useState("all"); // "all" | "super" | "admin"
  const [search, setSearch] = useState("");

  // Invite dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    username: "",
    email: "",
    brand_id: "",
    restaurant_id: "",
    is_super: false,
    send_invite: true,
  });

  // Edit dialog
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    brand_id: "",
    is_super: false,
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b, r] = await Promise.all([
        getAllUsers(),
        getAllBrands(),
        getAllRestaurants(),
      ]);
      setUsers(u.data?.data || []);
      setBrands(b.data?.data || []);
      setRestaurants(r.data?.data || []);
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Failed to load users", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const restaurantsForBrand = useMemo(
    () =>
      form.brand_id
        ? restaurants.filter((r) => r.brand_id === form.brand_id)
        : [],
    [restaurants, form.brand_id]
  );

  const brandName = (id) =>
    brands.find((b) => b.id === Number(id))?.name || null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (brandFilter === "unassigned" && u.brand_id) return false;
      if (
        brandFilter !== "all" &&
        brandFilter !== "unassigned" &&
        u.brand_id !== brandFilter
      )
        return false;
      if (roleFilter === "super" && !u.is_super) return false;
      if (roleFilter === "admin" && u.is_super) return false;
      if (q) {
        const hay = `${u.username || ""} ${u.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, brandFilter, roleFilter, search]);

  const handleInvite = async () => {
    setSaving(true);
    try {
      const username = form.username.trim() || form.email.split("@")[0];
      await createUser({
        username,
        email: form.email.trim(),
        brand_id: form.is_super ? undefined : form.brand_id || undefined,
        restaurant_id: form.send_invite
          ? form.restaurant_id || undefined
          : undefined,
        is_super: !!form.is_super,
        send_invite: !!form.send_invite,
      });
      enqueueSnackbar(
        form.send_invite
          ? `Invite sent to ${form.email}`
          : `Admin created (${form.email})`,
        { variant: "success" }
      );
      setOpen(false);
      setForm({
        username: "",
        email: "",
        brand_id: "",
        restaurant_id: "",
        is_super: false,
        send_invite: true,
      });
      load();
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Failed to create user", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await deleteUser(deleting.id);
      enqueueSnackbar("User deleted", { variant: "success" });
      setDeleting(null);
      load();
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Failed to delete user", {
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = (u) => {
    setEditForm({
      username: u.username || "",
      email: u.email || "",
      brand_id: u.brand_id || "",
      is_super: !!u.is_super,
    });
    setEditing(u);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await updateUser(editing.id, {
        username: editForm.username.trim(),
        email: editForm.email.trim() || null,
        brand_id: editForm.is_super ? null : editForm.brand_id || null,
        is_super: !!editForm.is_super,
      });
      enqueueSnackbar("User updated", { variant: "success" });
      setEditing(null);
      load();
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Failed to update user", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResendConfirm = async (restaurant_id) => {
    if (!resending) return;
    setResendLoading(true);
    try {
      await resendInvite(resending.id, restaurant_id);
      enqueueSnackbar(`Invite resent to ${resending.email}`, {
        variant: "success",
      });
      setResending(null);
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Failed to resend invite", {
        variant: "error",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Admin users
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          disabled={brands.length === 0}
        >
          Create admin
        </Button>
      </Box>


      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
        >
          <TextField
            placeholder="Search by username or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <MenuItem value="all">All brands</MenuItem>
              <MenuItem value="unassigned">Unassigned / super</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="super">Super admin</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "rgba(72,102,78,0.06)" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                    No users match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((u) => (
                <TableRow
                  key={u.id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography fontWeight={600}>{u.username}</Typography>
                      {u.id === currentUserId && (
                        <Chip label="you" size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: u.email ? "text.primary" : "text.disabled" }}>
                    {u.email || "—"}
                  </TableCell>
                  <TableCell>
                    {u.brand_id ? (
                      brandName(u.brand_id) || `Brand ${u.brand_id}`
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.is_super ? (
                      <Chip label="super admin" color="primary" size="small" />
                    ) : (
                      <Chip label="admin" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {u.email && (
                      <IconButton
                        size="small"
                        onClick={() => setResending(u)}
                        aria-label="Resend invite"
                        title="Resend invite"
                        sx={actionBtnSx("tertiary")}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => openEdit(u)}
                      aria-label="Edit"
                      title="Edit"
                      sx={actionBtnSx("primary")}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleting(u)}
                      aria-label="Delete"
                      disabled={u.id === currentUserId}
                      title={
                        u.id === currentUserId
                          ? "You can't delete your own account"
                          : "Delete"
                      }
                      sx={actionBtnSx("error")}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit dialog */}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 7 }}>
          Edit user
          <CloseDialogButton onClose={() => setEditing(null)} loading={saving} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={editForm.username}
              onChange={(e) =>
                setEditForm({ ...editForm, username: e.target.value })
              }
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(e) =>
                setEditForm({ ...editForm, email: e.target.value })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.is_super}
                  disabled={editing?.id === currentUserId}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      is_super: e.target.checked,
                      brand_id: e.target.checked ? "" : editForm.brand_id,
                    })
                  }
                />
              }
              label={
                editing?.id === currentUserId
                  ? "Super admin (cannot change your own role)"
                  : "Super admin (full cross-brand access)"
              }
            />
            {!editForm.is_super && (
              <FormControl fullWidth>
                <InputLabel>Brand (auth scope)</InputLabel>
                <Select
                  label="Brand (auth scope)"
                  value={editForm.brand_id || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, brand_id: e.target.value })
                  }
                >
                  <MenuItem value="">
                    <em>Unassigned (cannot log in)</em>
                  </MenuItem>
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={saving || !editForm.username}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 7 }}>
          Create admin user
          <CloseDialogButton onClose={() => setOpen(false)} loading={saving} />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Username (optional — defaults to email prefix)"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_super}
                  onChange={(e) =>
                    setForm({ ...form, is_super: e.target.checked })
                  }
                />
              }
              label="Super admin (full cross-brand access)"
            />
            {!form.is_super && (
              <FormControl fullWidth required>
                <InputLabel>Brand (auth scope)</InputLabel>
                <Select
                  label="Brand (auth scope)"
                  value={form.brand_id || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      brand_id: e.target.value,
                      restaurant_id: "",
                    })
                  }
                >
                  {brands.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={form.send_invite}
                  onChange={(e) =>
                    setForm({ ...form, send_invite: e.target.checked })
                  }
                />
              }
              label="Send invite email"
            />
            {form.send_invite && (
              <>
                {form.is_super && (
                  <FormControl fullWidth required>
                    <InputLabel>Brand (for invite link only)</InputLabel>
                    <Select
                      label="Brand (for invite link only)"
                      value={form.brand_id || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          brand_id: e.target.value,
                          restaurant_id: "",
                        })
                      }
                    >
                      {brands.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth required disabled={!form.brand_id}>
                  <InputLabel>Branch (initial restaurant)</InputLabel>
                  <Select
                    label="Branch (initial restaurant)"
                    value={form.restaurant_id || ""}
                    onChange={(e) =>
                      setForm({ ...form, restaurant_id: e.target.value })
                    }
                  >
                    {restaurantsForBrand.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.restaurant_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {!form.send_invite
                ? "No email will be sent. The user can log in at /login with their email and OTP."
                : form.is_super
                ? "Super admins have full cross-brand access regardless of the brand picked here — it's only used as the starting context for the invite link."
                : "The invitee will receive an email with a 48-hour login link to the selected branch. Their account is scoped to all branches under the brand."}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={
              saving ||
              !form.email ||
              (!form.is_super && !form.brand_id) ||
              (form.send_invite && (!form.brand_id || !form.restaurant_id))
            }
          >
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleting?.username || "this user"}?`}
        message={
          deleting?.is_super
            ? "This is a super admin account. They will lose all access immediately."
            : "This user will lose access immediately. This cannot be undone."
        }
        loading={deleteLoading}
      />

      <ResendInviteDialog
        open={resending !== null}
        onClose={() => setResending(null)}
        onConfirm={handleResendConfirm}
        user={resending}
        brands={brands}
        restaurants={restaurants}
        loading={resendLoading}
      />
    </Box>
  );
};

export default UsersPanel;
